import { notify, getApiErrorMessage } from '@/lib/toast';
import { openPrintWindow } from '@/lib/print-document';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import toast from 'react-hot-toast';
import { Power, PowerOff, Truck, Plus, ShoppingBag, CheckCircle, User, Pencil, Trash2, Crosshair, Loader2, Package, Car, Map } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TabBar } from '@/components/ui/tab-bar';
import { FilterChips } from '@/components/ui/filter-chips';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { isValidPkMobile } from '@/lib/pk-mobile';
import { phoneForSaleChange, feeForSaleChange } from '@/lib/delivery-form';

const DELIVERY_STATUSES = ['PENDING', 'ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'DELIVERED', 'FAILED', 'CANCELLED'] as const;

// POS-I157: mirrors the API's allowed moves; Delivered and Cancelled are final, Failed can be retried.
const NEXT_STATUSES: Record<string, string[]> = {
  PENDING: ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'FAILED', 'CANCELLED'],
  ASSIGNED: ['PENDING', 'PICKED_UP', 'IN_TRANSIT', 'DELIVERED', 'FAILED', 'CANCELLED'],
  PICKED_UP: ['IN_TRANSIT', 'DELIVERED', 'FAILED', 'CANCELLED'],
  IN_TRANSIT: ['DELIVERED', 'FAILED', 'CANCELLED'],
  DELIVERED: [],
  FAILED: ['PENDING', 'ASSIGNED', 'CANCELLED'],
  CANCELLED: [],
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-warning/10 text-warning-text',
  ASSIGNED: 'bg-info/10 text-info-text',
  PICKED_UP: 'bg-primary/10 text-primary-text',
  IN_TRANSIT: 'bg-primary/10 text-primary-text',
  DELIVERED: 'bg-success/10 text-success-text',
  FAILED: 'bg-destructive/10 text-destructive-text',
  CANCELLED: 'bg-muted text-muted-foreground',
};

const emptyDeliveryForm = () => ({ orderType: 'DELIVERY' as 'DELIVERY' | 'CLICK_AND_COLLECT', saleId: '', address: '', city: '', phone: '', phoneEdited: false, deliveryFee: 0, paymentMode: 'PREPAID' as 'PREPAID' | 'COD', feeBilled: true, notes: '', pickupLocationId: '', pickupReadyAt: '', zoneId: '', feeReason: '' });
const emptyDriverForm = () => ({ name: '', phone: '', email: '', vehicleType: '', vehicleNo: '', licenseNo: '', notes: '', isActive: true });

type DriverFormValues = ReturnType<typeof emptyDriverForm>;

/** Omit blank optional strings so class-validator @IsOptional() applies on the API. */
function driverPayloadFromForm(d: DriverFormValues): Record<string, string> {
  const payload: Record<string, string> = { name: d.name.trim(), phone: d.phone.trim() };
  const optional: ('email' | 'vehicleType' | 'vehicleNo' | 'licenseNo' | 'notes')[] = ['email', 'vehicleType', 'vehicleNo', 'licenseNo', 'notes'];
  for (const key of optional) {
    const v = d[key].trim();
    if (v) payload[key] = v;
  }
  return payload;
}
const emptyZoneForm = () => ({ name: '', cities: '', deliveryFee: 0, freeAboveAmount: '', estimatedMinutes: '', isActive: true, minOrderAmount: '', hoursFrom: '', hoursTo: '', deliveryDays: [] as number[] });

// POS-I177: 0 = Sunday .. 6 = Saturday, shown Monday first
const WEEKDAYS: { d: number; label: string }[] = [
  { d: 1, label: 'Mon' }, { d: 2, label: 'Tue' }, { d: 3, label: 'Wed' }, { d: 4, label: 'Thu' }, { d: 5, label: 'Fri' }, { d: 6, label: 'Sat' }, { d: 0, label: 'Sun' },
];
const zoneHoursText = (z: { hoursFrom?: string | null; hoursTo?: string | null; deliveryDays?: number[] | null }) => {
  const days = [...new Set(z.deliveryDays ?? [])];
  const parts: string[] = [];
  if (z.hoursFrom && z.hoursTo) parts.push(`${z.hoursFrom}-${z.hoursTo}`);
  if (days.length && days.length < 7) parts.push(WEEKDAYS.filter((w) => days.includes(w.d)).map((w) => w.label).join(', '));
  return parts.join(' · ');
};

/** POS-I175: Active / Inactive / All list filter for drivers and zones. */
type ActiveFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
const ACTIVE_FILTER_CHIPS = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'INACTIVE', label: 'Inactive' },
];
const passesActiveFilter = (isActive: boolean, f: ActiveFilter) => f === 'ALL' || (f === 'ACTIVE') === isActive;

interface Paged<T> { data: T[]; total: number }

interface DeliveryOrder {
  id: string;
  orderType: 'DELIVERY' | 'CLICK_AND_COLLECT';
  status: string;
  source?: string | null;
  orderNo?: string | null;
  sale?: { invoiceNumber: string; totalAmount?: string | number | null; customer?: { name: string; phone?: string | null } | null } | null;
  pickupLocationId?: string | null;
  address?: string | null;
  city?: string | null;
  pickupReadyAt?: string | null;
  pickedUpAt?: string | null;
  deliveredAt?: string | null;
  deliveryFee?: string | number | null;
  feeReason?: string | null;
  driver?: { id: string; name: string } | null;
  customerPhone?: string | null;
  paymentMode?: 'PREPAID' | 'COD';
  feeBilled?: boolean;
  cod?: CodInfo | null;
  proof?: { receivedBy: string | null; note: string | null; fileKey: string | null; fileName: string | null; recordedAt: string | null } | null;
  lastNotice?: { eventKey: string | null; text: string; at: string } | null;
  createdAt: string;
}

interface CodInfo {
  paymentMode: 'PREPAID' | 'COD';
  feeBilled: boolean;
  saleOutstanding: number;
  feeOutstanding: number;
  amountToCollect: number;
  cashCollected: number;
  deferredReason: string | null;
}

interface DeliveryDriver {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  vehicleType?: string | null;
  vehicleNo?: string | null;
  licenseNo?: string | null;
  notes?: string | null;
  isActive: boolean;
  _count?: { deliveryOrders: number };
  /** POS-I171: orders that are not Delivered or Cancelled */
  openOrders?: number;
}

interface DeliveryZone {
  id: string;
  name: string;
  cities?: string[] | null;
  deliveryFee: number;
  freeAboveAmount?: number | null;
  estimatedMinutes?: number | null;
  isActive: boolean;
  minOrderAmount?: number | string | null;
  hoursFrom?: string | null;
  hoursTo?: string | null;
  deliveryDays?: number[] | null;
}

interface SaleForDelivery {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  customer?: { name: string; phone?: string | null } | null;
}

interface PickupLocation { id: string; name: string }

interface ZoneLookupResult {
  zone: { id: string; name: string } | null;
  matched?: boolean;
  /** POS-I170: exact = the city is listed in the zone; contains = the typed text contains a listed area */
  matchType?: 'exact' | 'contains' | null;
  otherMatches?: string[];
  calculatedFee: number | null;
  isFreeDelivery?: boolean;
  estimatedMinutes?: number | null;
}

interface ZoneUpdatePayload {
  id: string;
  name: string;
  cities: string[];
  deliveryFee: number;
  freeAboveAmount?: number;
  estimatedMinutes?: number;
  isActive?: boolean;
  minOrderAmount?: number | null;
  hoursFrom?: string | null;
  hoursTo?: string | null;
  deliveryDays?: number[];
}

type OrderTypeTab = 'ALL' | 'DELIVERY' | 'CLICK_AND_COLLECT';
type MainTab = 'orders' | 'drivers' | 'zones';

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** True when this dialog still has cash to collect (COD order with an amount due). */
function cashDialogCodDue(d: { order: DeliveryOrder }): boolean {
  return d.order.paymentMode === 'COD' && (d.order.cod?.amountToCollect ?? 0) > 0;
}

async function fetchProofImage(fileKey: string): Promise<Blob> {
  return api.get<Blob>(`/uploads/view?key=${encodeURIComponent(fileKey)}`, { responseType: 'blob' }).then((r) => r.data);
}

const blobToDataUrl = (b: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });

interface DeliveryNoteData {
  business: string; storeName?: string | null; storeAddress?: string | null; storePhone?: string | null; invoiceNumber: string; orderType: string; createdAt: string; deliveredAt: string | null;
  customerName: string; customerPhone: string | null; address: string; city: string | null; zone: string | null;
  driver: { name: string; phone: string } | null; notes: string | null;
  items: Array<{ name: string; sku: string; quantity: number; uom: string | null }>;
  totalAmount: number; deliveryFee: number; paymentMode: string; feeBilled: boolean; cashCollected: number;
  amountToCollect: number; codDeferredReason: string | null;
  proof: { receivedBy: string | null; note: string | null; fileKey: string | null; fileName: string | null; recordedAt: string; recordedBy: string | null } | null;
}

/** POS-I160 (C): printable delivery note with items, cash and the proof of delivery. */
async function printDeliveryNote(orderId: string) {
  const d = await api.get<DeliveryNoteData>(`/delivery/${orderId}/note`).then((r) => r.data);
  let img = '';
  if (d.proof?.fileKey) {
    try { img = await blobToDataUrl(await fetchProofImage(d.proof.fileKey)); } catch { img = ''; }
  }
  const rows = d.items.map((i) => `<tr><td>${esc(i.name)}<div class="s">${esc(i.sku)}</div></td><td class="r">${esc(i.quantity)}${i.uom ? ' ' + esc(i.uom) : ''}</td></tr>`).join('');
  const cod = d.paymentMode === 'COD'
    ? `<div class="box"><b>Cash on delivery</b><br/>${d.amountToCollect > 0 ? `<b>Amount to collect: ${esc(formatCurrency(d.amountToCollect))}</b>` : 'Fully collected'}${d.cashCollected > 0 ? ` · collected so far ${esc(formatCurrency(d.cashCollected))}` : ''}${d.codDeferredReason ? `<br/>Collected later: ${esc(d.codDeferredReason)}` : ''}</div>`
    : '<div class="box"><b>Prepaid</b></div>';
  const signature = '<div class="box" style="margin-top:28px"><b>Received by</b><br/><br/><br/>Name: ____________________ &nbsp; Signature: ____________________ &nbsp; Date: ______________</div>';
  const proof = d.proof
    ? `<div class="box"><b>Proof of delivery</b><br/>Received by ${esc(d.proof.receivedBy ?? '')}${d.proof.note ? '<br/>' + esc(d.proof.note) : ''}<br/>Recorded ${esc(formatDate(d.proof.recordedAt))}${d.proof.recordedBy ? ' by ' + esc(d.proof.recordedBy) : ''}${img ? `<br/><img src="${img}" style="max-width:260px;max-height:160px;margin-top:6px"/>` : ''}</div>`
    : '';
  // POS-I240: shared printable-document builder (Powered by footer on every page).
  if (!openPrintWindow({ title: `Delivery note ${d.invoiceNumber}`, width: 800, height: 900, extraCss: 'h1{margin:0}h2{font-size:14px;margin:0 0 12px;color:#555;font-weight:500}.s{font-size:11px;color:#777}.box{border:1px solid #d1d5db;border-radius:6px;padding:10px;margin:10px 0;font-size:13px}.two{display:flex;gap:16px}.two>div{flex:1}', bodyHtml: `<h1>${esc(d.business)}</h1>${d.storeName || d.storeAddress || d.storePhone ? `<div class="s">${[d.storeName && d.storeName !== d.business ? esc(d.storeName) : '', esc(d.storeAddress ?? ''), d.storePhone ? 'Tel: ' + esc(d.storePhone) : ''].filter(Boolean).join(' · ')}</div>` : ''}<h2>Delivery note · ${esc(d.invoiceNumber)} · ${esc(formatDate(d.createdAt))}</h2><div class="two"><div class="box"><b>Deliver to</b><br/>${esc(d.customerName)}${d.customerPhone ? '<br/>' + esc(d.customerPhone) : ''}<br/>${esc([d.address, d.city].filter(Boolean).join(', '))}${d.zone ? '<br/>Zone: ' + esc(d.zone) : ''}</div><div class="box"><b>Rider</b><br/>${d.driver ? esc(d.driver.name) + '<br/>Phone: ' + esc(d.driver.phone) : 'Not assigned'}${d.deliveredAt ? '<br/>Delivered ' + esc(formatDate(d.deliveredAt)) : ''}</div></div><table><thead><tr><th>Item</th><th class="r">Qty</th></tr></thead><tbody>${rows}</tbody></table>${d.notes ? '<div class="box"><b>Instructions</b><br/>' + esc(d.notes) + '</div>' : ''}${cod}${proof}${signature}` })) { toast.error('Allow pop-ups to print the delivery note'); return; }
}

export function DeliveryPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission('delivery:read') || hasPermission('delivery:*');
  const canCreate = hasPermission('delivery:create') || hasPermission('delivery:*');
  const canManage = hasPermission('delivery:manage') || hasPermission('delivery:*');

  const STATUS_LABELS: Record<string, string> = {
    PENDING: t('deliveryPending'),
    ASSIGNED: t('deliveryAssigned'),
    PICKED_UP: t('deliveryPickedUp'),
    IN_TRANSIT: t('deliveryInTransit'),
    DELIVERED: t('deliveryDelivered'),
    FAILED: t('deliveryFailed'),
    CANCELLED: t('deliveryCancelled'),
  };

  const [mainTab, setMainTab] = useState<MainTab>('orders');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [orderTypeTab, setOrderTypeTab] = useState<OrderTypeTab>('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyDeliveryForm());
  const [confirmStatusChange, setConfirmStatusChange] = useState<{ id: string; status: string; reason: string } | null>(null);
  const [historyOrderId, setHistoryOrderId] = useState<string | null>(null);
  // POS-I160 (B): cash-on-delivery dialog. deliverAfter = the dispatcher is marking the order Delivered.
  const [cashDialog, setCashDialog] = useState<{ order: DeliveryOrder; deliverAfter: boolean; mode: 'collect' | 'later'; amount: string; note: string; laterReason: string; receivedBy: string; proofNote: string; proofFile: File | null } | null>(null);
  const openCashDialog = (order: DeliveryOrder, deliverAfter: boolean) =>
    setCashDialog({ order, deliverAfter, mode: 'collect', amount: String(order.cod?.amountToCollect ?? ''), note: '', laterReason: '', receivedBy: '', proofNote: '', proofFile: null });
  const [detectingZone, setDetectingZone] = useState(false);
  const [zoneHint, setZoneHint] = useState<string | null>(null);
  // POS-I170: shown when auto-detect finds no zone for the city (never a guessed zone)
  const [zoneWarn, setZoneWarn] = useState<string | null>(null);
  // POS-I177: set when the API says the order is outside the zone's delivery hours
  const [outsideHoursAsk, setOutsideHoursAsk] = useState<string | null>(null);

  // Driver state
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<DeliveryDriver | null>(null);
  const [driverForm, setDriverForm] = useState(emptyDriverForm());
  const [confirmDeleteDriver, setConfirmDeleteDriver] = useState<string | null>(null);
  // POS-I175: Active/Inactive/All filters and the confirmation when a rider with open orders is deactivated
  const [driverFilter, setDriverFilter] = useState<ActiveFilter>('ALL');
  const [zoneFilter, setZoneFilter] = useState<ActiveFilter>('ALL');
  const [confirmDeactivateDriver, setConfirmDeactivateDriver] = useState<{ driver: DeliveryDriver; form: DriverFormValues | null } | null>(null);
  const [assignDriverDialog, setAssignDriverDialog] = useState<{ orderId: string; invoice: string; currentDriverId: string | null } | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string>('');

  // Zone state
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<DeliveryZone | null>(null);
  const [zoneForm, setZoneForm] = useState(emptyZoneForm());
  const [confirmDeleteZone, setConfirmDeleteZone] = useState<string | null>(null);

  const qc = useQueryClient();
  const handleDeliveryUpdate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
    qc.invalidateQueries({ queryKey: ['sales-for-delivery'] });
    qc.invalidateQueries({ queryKey: ['delivery-history'] });
  }, [qc]);
  useSocketEvent('sale.created', handleDeliveryUpdate);
  useSocketEvent('dining_order.billed', handleDeliveryUpdate);
  useSocketEvent('delivery.updated', handleDeliveryUpdate);

  const orderTypeFilter = orderTypeTab === 'ALL' ? '' : orderTypeTab;

  const { data, isLoading } = useQuery<Paged<DeliveryOrder>>({
    queryKey: ['delivery-orders', page, statusFilter, orderTypeFilter],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      if (orderTypeFilter) params.set('orderType', orderTypeFilter);
      return api.get<Paged<DeliveryOrder>>(`/delivery?${params}`).then((r) => r.data);
    },
    enabled: canRead && mainTab === 'orders',
  });

  const { data: salesData } = useQuery<Paged<SaleForDelivery>>({
    queryKey: ['sales-for-delivery'],
    queryFn: () => api.get<Paged<SaleForDelivery>>('/sales?limit=100&status=COMPLETED').then((r) => r.data),
    enabled: dialogOpen,
  });
  // POS-I154: sales that already have a delivery order are marked and cannot be picked again.
  const { data: deliveredSaleIds } = useQuery<string[]>({
    queryKey: ['sales-for-delivery', 'taken'],
    queryFn: () => api.get<string[]>('/delivery/sale-ids').then((r) => r.data),
    enabled: dialogOpen,
  });
  const takenSales = new Set(deliveredSaleIds ?? []);
  const saleOptions = (salesData?.data ?? []).map((s: SaleForDelivery) => ({
    value: s.id,
    disabled: takenSales.has(s.id),
    label: `${s.invoiceNumber} — ${s.customer?.name ?? 'Walk-in'} (${formatCurrency(s.totalAmount)})${takenSales.has(s.id) ? ' — already has a delivery order' : ''}`,
  }));

  const { data: locationsData } = useQuery<PickupLocation[] | { data: PickupLocation[] }>({
    queryKey: ['locations-for-pickup'],
    queryFn: () => api.get<PickupLocation[] | { data: PickupLocation[] }>('/locations').then((r) => r.data),
    // POS-I168: loaded with the page (not only when the dialog opens) so list rows show the store name at once.
    enabled: canRead,
    staleTime: 60_000,
  });
  const locationRows: PickupLocation[] = Array.isArray(locationsData) ? locationsData : locationsData?.data ?? [];
  const locationOptions = locationRows.map((l: PickupLocation) => ({ value: l.id, label: l.name }));

  // Drivers queries
  const { data: driversData, isLoading: driversLoading } = useQuery<Paged<DeliveryDriver>>({
    queryKey: ['delivery-drivers'],
    queryFn: () => api.get<Paged<DeliveryDriver>>('/delivery-drivers').then((r) => r.data),
    enabled: canRead,
  });
  const driverOptions = (driversData?.data ?? [])
    .filter((d: DeliveryDriver) => d.isActive)
    .map((d: DeliveryDriver) => ({ value: d.id, label: `${d.name} (${d.phone})` }));

  // Zones queries
  const { data: zonesData, isLoading: zonesLoading } = useQuery<Paged<DeliveryZone>>({
    queryKey: ['delivery-zones'],
    queryFn: () => api.get<Paged<DeliveryZone>>('/delivery-zones').then((r) => r.data),
    enabled: canRead,
  });
  const zoneOptions = (zonesData?.data ?? [])
    .filter((z: DeliveryZone) => z.isActive)
    .map((z: DeliveryZone) => ({ value: z.id, label: `${z.name} — ${formatCurrency(z.deliveryFee)}` }));

  // POS-I160 (A): per-store switch for the customer SMS notices.
  const { data: deliverySettings } = useQuery<{ noticesEnabled: boolean }>({
    queryKey: ['delivery-settings'],
    queryFn: () => api.get<{ noticesEnabled: boolean }>('/delivery/settings').then((r) => r.data),
    enabled: canRead,
  });
  const noticesMutation = useMutation({
    mutationFn: (noticesEnabled: boolean) => api.patch('/delivery/settings', { noticesEnabled }),
    onSuccess: (_r, enabled) => {
      toast.success(enabled ? 'Customer notices are on' : 'Customer notices are off');
      qc.invalidateQueries({ queryKey: ['delivery-settings'] });
    },
    onError: (err) => notify.error(err, 'Failed to update the setting'),
  });

  const createZoneMutation = useMutation({
    mutationFn: (d: ReturnType<typeof emptyZoneForm>) => api.post('/delivery-zones', {
      name: d.name,
      cities: d.cities ? d.cities.split(',').map((c: string) => c.trim()).filter(Boolean) : [],
      deliveryFee: Number(d.deliveryFee),
      ...(d.freeAboveAmount && { freeAboveAmount: Number(d.freeAboveAmount) }),
      ...(d.estimatedMinutes && { estimatedMinutes: Number(d.estimatedMinutes) }),
      ...(d.minOrderAmount && { minOrderAmount: Number(d.minOrderAmount) }),
      ...(d.hoursFrom && d.hoursTo && { hoursFrom: d.hoursFrom, hoursTo: d.hoursTo }),
      ...(d.deliveryDays.length > 0 && { deliveryDays: d.deliveryDays }),
    }),
    onSuccess: () => { toast.success('Zone created'); qc.invalidateQueries({ queryKey: ['delivery-zones'] }); setZoneDialogOpen(false); setZoneForm(emptyZoneForm()); },
    onError: (err) => notify.error(err, 'Failed to create zone'),
  });

  const updateZoneMutation = useMutation({
    mutationFn: ({ id, ...rest }: ZoneUpdatePayload) => api.patch(`/delivery-zones/${id}`, rest),
    onSuccess: () => { toast.success('Zone updated'); qc.invalidateQueries({ queryKey: ['delivery-zones'] }); setZoneDialogOpen(false); setEditingZone(null); setZoneForm(emptyZoneForm()); },
    onError: (err) => notify.error(err, 'Failed to update zone'),
  });

  const setZoneActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/delivery-zones/${id}`, { isActive }),
    onSuccess: (_r, v) => { toast.success(v.isActive ? 'Zone activated' : 'Zone deactivated'); qc.invalidateQueries({ queryKey: ['delivery-zones'] }); },
    onError: (err) => notify.error(err, 'Failed to change the zone'),
  });

  const deleteZoneMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/delivery-zones/${id}`),
    onSuccess: () => { toast.success('Zone deleted'); qc.invalidateQueries({ queryKey: ['delivery-zones'] }); },
    onError: (err) => notify.error(err, 'Failed to delete zone'),
  });

  const handleEditZone = (zone: DeliveryZone) => {
    setEditingZone(zone);
    setZoneForm({ name: zone.name, cities: (zone.cities ?? []).join(', '), deliveryFee: Number(zone.deliveryFee), freeAboveAmount: zone.freeAboveAmount ? String(zone.freeAboveAmount) : '', estimatedMinutes: zone.estimatedMinutes ? String(zone.estimatedMinutes) : '', isActive: zone.isActive, minOrderAmount: zone.minOrderAmount ? String(Number(zone.minOrderAmount)) : '', hoursFrom: zone.hoursFrom ?? '', hoursTo: zone.hoursTo ?? '', deliveryDays: zone.deliveryDays ?? [] });
    setZoneDialogOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: ({ d, confirmOutsideHours }: { d: ReturnType<typeof emptyDeliveryForm>; confirmOutsideHours?: boolean }) =>
      api.post('/delivery', {
        saleId: d.saleId,
        ...(confirmOutsideHours && { confirmOutsideHours: true }),
        address: d.orderType === 'DELIVERY' ? d.address : (d.address || 'N/A'),
        customerPhone: d.phone.trim(),
        ...(d.orderType === 'DELIVERY' && (d.deliveryFee > 0 || !!d.zoneId) && { deliveryFee: Number(d.deliveryFee) || 0 }),
        ...(d.orderType === 'DELIVERY' && d.feeReason.trim() && { feeReason: d.feeReason.trim() }),
        ...(d.orderType === 'DELIVERY' && d.paymentMode === 'COD' && { paymentMode: 'COD', feeBilled: d.feeBilled }),
        ...(d.orderType === 'DELIVERY' && d.city.trim() && { city: d.city.trim() }),
        notes: d.notes || undefined,
        orderType: d.orderType,
        ...(d.orderType === 'CLICK_AND_COLLECT' && d.pickupLocationId && { pickupLocationId: d.pickupLocationId }),
        ...(d.orderType === 'CLICK_AND_COLLECT' && d.pickupReadyAt && { pickupReadyAt: d.pickupReadyAt }),
        ...(d.zoneId && { zoneId: d.zoneId }),
      }),
    onSuccess: () => {
      toast.success(t('deliveryOrderCreated'));
      qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
      setDialogOpen(false);
      setForm(emptyDeliveryForm());
      setZoneHint(null);
      setZoneWarn(null);
    },
    onError: (err) => {
      // POS-I177: outside the zone's delivery hours is a warning the cashier can confirm
      const data = (err as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
      if (data?.errors?.includes('OUTSIDE_DELIVERY_HOURS')) { setOutsideHoursAsk(data.message ?? 'Outside delivery hours. Create the order anyway?'); return; }
      notify.error(err, t('failedToCreateDelivery'));
    },
  });

  const autoDetectZone = async () => {
    if (!form.city.trim()) { toast.error('Enter a city first'); return; }
    setDetectingZone(true);
    try {
      const sale = (salesData?.data ?? []).find((s: SaleForDelivery) => s.id === form.saleId);
      const qs = new URLSearchParams({ city: form.city.trim() });
      if (sale?.totalAmount != null) qs.set('orderAmount', String(sale.totalAmount));
      const res = await api.get<ZoneLookupResult>(`/delivery-zones/lookup?${qs}`).then((r) => r.data);
      const zone = res?.zone;
      if (!zone) {
        // POS-I170: never guess. The cashier picks a zone by hand, or enters a fee with a reason.
        setZoneHint(null);
        setZoneWarn(`No delivery zone covers ${form.city.trim()}. Choose a zone below, or enter the delivery fee by hand with a short reason.`);
        setForm((f) => ({ ...f, zoneId: '', feeReason: '' }));
        return;
      }
      const fee = res.calculatedFee ?? 0;
      setForm((f) => ({ ...f, zoneId: zone.id, deliveryFee: fee, feeReason: '' }));
      setZoneWarn(null);
      const also = res.otherMatches?.length ? ` · also listed in ${res.otherMatches.join(', ')}` : '';
      const how = res.matchType === 'exact' ? ' (city listed in this zone)' : '';
      setZoneHint(
        (res.isFreeDelivery
          ? `Matched “${zone.name}”${how} · Free delivery`
          : `Matched “${zone.name}”${how} · ${formatCurrency(fee)}${res.estimatedMinutes ? ` · ~${res.estimatedMinutes} min` : ''}`) + also,
      );
      toast.success('Delivery zone detected');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not detect zone'));
    } finally {
      setDetectingZone(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; status?: string; reason?: string; pickupReadyAt?: string; pickedUp?: boolean; collectLaterReason?: string }) =>
      api.patch(`/delivery/${payload.id}`, {
        ...(payload.status && { status: payload.status }),
        ...(payload.reason && { reason: payload.reason }),
        ...(payload.pickupReadyAt && { pickupReadyAt: payload.pickupReadyAt }),
        ...(payload.pickedUp && { pickedUp: true }),
        ...(payload.collectLaterReason && { collectLaterReason: payload.collectLaterReason }),
      }),
    onSuccess: () => { toast.success(t('statusUpdated')); qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] }); qc.invalidateQueries({ queryKey: ['delivery-history'] }); },
    onError: (err) => notify.error(err, t('failedToUpdateStatus')),
  });

  const collectCashMutation = useMutation({
    mutationFn: async (v: { id: string; amount: number; note?: string; deliverAfter: boolean; laterReason?: string; mode: 'collect' | 'later'; codDue: boolean; receivedBy: string; proofNote: string; proofFile: File | null }) => {
      if (v.codDue && v.mode === 'collect') {
        await api.post(`/delivery/${v.id}/collect-cash`, { amount: v.amount, ...(v.note && { note: v.note }) });
      }
      if (v.deliverAfter) {
        // POS-I160 (C): the photo or signature image goes to object storage first; the order keeps its key.
        let proof: { proofFileKey?: string; proofFileName?: string } = {};
        if (v.proofFile) {
          const fd = new FormData();
          fd.append('file', v.proofFile);
          const up = await api.post<{ key: string; fileName: string }>('/uploads', fd, { params: { folder: 'delivery-proofs' }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
          proof = { proofFileKey: up.key, proofFileName: up.fileName };
        }
        await api.patch(`/delivery/${v.id}`, {
          status: 'DELIVERED',
          ...(v.receivedBy.trim() && { receivedBy: v.receivedBy.trim() }),
          ...(v.proofNote.trim() && { proofNote: v.proofNote.trim() }),
          ...proof,
          ...(v.codDue && v.mode === 'later' && { collectLaterReason: v.laterReason }),
        });
      }
    },
    onSuccess: (_r, v) => {
      toast.success(v.deliverAfter ? t('statusUpdated') : 'Cash collected');
      qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
      qc.invalidateQueries({ queryKey: ['delivery-history'] });
      setCashDialog(null);
    },
    onError: (err) => { notify.error(err, 'Could not record the cash'); qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] }); },
  });

  const createDriverMutation = useMutation({
    mutationFn: (d: DriverFormValues) => api.post('/delivery-drivers', driverPayloadFromForm(d)),
    onSuccess: () => {
      toast.success('Driver created');
      qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
      setDriverDialogOpen(false);
      setDriverForm(emptyDriverForm());
    },
    onError: (err) => notify.error(err, 'Failed to create driver'),
  });

  const updateDriverMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: DriverFormValues }) =>
      api.patch(`/delivery-drivers/${id}`, { ...driverPayloadFromForm(form), isActive: form.isActive }),
    onSuccess: () => {
      toast.success('Driver updated');
      qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
      setDriverDialogOpen(false);
      setEditingDriver(null);
      setDriverForm(emptyDriverForm());
    },
    onError: (err) => notify.error(err, 'Failed to update driver'),
  });

  const setDriverActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/delivery-drivers/${id}`, { isActive }),
    onSuccess: (_r, v) => { toast.success(v.isActive ? 'Driver activated' : 'Driver deactivated'); qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] }); },
    onError: (err) => notify.error(err, 'Failed to change the driver'),
  });

  const deleteDriverMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/delivery-drivers/${id}`),
    onSuccess: () => { toast.success('Driver removed'); qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] }); },
    onError: (err) => notify.error(err, 'Failed to delete driver'),
  });

  const assignDriverMutation = useMutation({
    mutationFn: ({ orderId, driverId }: { orderId: string; driverId: string | null }) =>
      // POS-I156: the server moves PENDING/ASSIGNED/FAILED orders with the driver change and keeps
      // the status of an order already on the road, so no status is sent from here.
      api.patch(`/delivery/${orderId}`, { assignedToId: driverId }),
    onSuccess: (_res, vars) => {
      toast.success(vars.driverId ? 'Driver assigned' : 'Driver unassigned');
      qc.invalidateQueries({ queryKey: ['delivery-orders'] }); qc.invalidateQueries({ queryKey: ['delivery-drivers'] });
      qc.invalidateQueries({ queryKey: ['delivery-history'] });
      setAssignDriverDialog(null);
      setSelectedDriver('');
    },
    onError: (err) => notify.error(err, 'Failed to assign driver'),
  });

  // POS-I173: the rider's phone must be a Pakistani mobile (an unchanged saved number can still be kept on edit)
  const driverPhoneOk = isValidPkMobile(driverForm.phone) || (!!editingDriver && driverForm.phone.trim() === editingDriver.phone);

  // POS-I178: a mistyped rider email is explained under the field (empty stays allowed; an unchanged saved email can be kept)
  const driverEmailOk = !driverForm.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(driverForm.email.trim()) || (!!editingDriver && driverForm.email.trim() === (editingDriver.email ?? ''));

  const driverSaveDisabled = createDriverMutation.isPending || updateDriverMutation.isPending || !driverForm.name.trim() || !driverPhoneOk || !driverEmailOk;
  const saveDriver = () => {
    if (driverSaveDisabled) return;
    if (!editingDriver) { createDriverMutation.mutate(driverForm); return; }
    // POS-I175: switching a rider off while they hold open orders asks first
    if (editingDriver.isActive && !driverForm.isActive && (editingDriver.openOrders ?? 0) > 0) {
      setConfirmDeactivateDriver({ driver: editingDriver, form: driverForm });
      return;
    }
    updateDriverMutation.mutate({ id: editingDriver.id, form: driverForm });
  };

  // POS-I176: a negative amount is explained under the field instead of the server's raw text
  const zoneFeeNegative = Number(zoneForm.deliveryFee) < 0;
  const zoneFreeNegative = Number(zoneForm.freeAboveAmount || 0) < 0;
  const zoneMinNegative = Number(zoneForm.minOrderAmount || 0) < 0;
  const zoneHoursHalf = !!zoneForm.hoursFrom !== !!zoneForm.hoursTo;
  const zoneSaveDisabled = createZoneMutation.isPending || updateZoneMutation.isPending || !zoneForm.name.trim() || zoneFeeNegative || zoneFreeNegative || zoneMinNegative || zoneHoursHalf;
  const saveZone = () => {
    if (zoneSaveDisabled) return;
    if (editingZone) {
      updateZoneMutation.mutate({ id: editingZone.id, name: zoneForm.name.trim(), cities: zoneForm.cities.split(',').map((c: string) => c.trim()).filter(Boolean), deliveryFee: Number(zoneForm.deliveryFee), ...(zoneForm.freeAboveAmount && { freeAboveAmount: Number(zoneForm.freeAboveAmount) }), ...(zoneForm.estimatedMinutes && { estimatedMinutes: Number(zoneForm.estimatedMinutes) }), isActive: zoneForm.isActive, minOrderAmount: zoneForm.minOrderAmount ? Number(zoneForm.minOrderAmount) : null, hoursFrom: zoneForm.hoursFrom && zoneForm.hoursTo ? zoneForm.hoursFrom : null, hoursTo: zoneForm.hoursFrom && zoneForm.hoursTo ? zoneForm.hoursTo : null, deliveryDays: zoneForm.deliveryDays });
    } else {
      createZoneMutation.mutate(zoneForm);
    }
  };
  /** Enter in a single-line field saves the dialog (POS-I176). */
  const submitOnEnter = (save: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); save(); }
  };

  /** POS-I175: deactivating a rider who still has open orders asks first; the orders stay with that rider. */
  const requestDriverActive = (driver: DeliveryDriver, isActive: boolean) => {
    if (!isActive && (driver.openOrders ?? 0) > 0) setConfirmDeactivateDriver({ driver, form: null });
    else setDriverActiveMutation.mutate({ id: driver.id, isActive });
  };

  const handleEditDriver = (driver: DeliveryDriver) => {
    setEditingDriver(driver);
    setDriverForm({ name: driver.name, phone: driver.phone, email: driver.email ?? '', vehicleType: driver.vehicleType ?? '', vehicleNo: driver.vehicleNo ?? '', licenseNo: driver.licenseNo ?? '', notes: driver.notes ?? '', isActive: driver.isActive });
    setDriverDialogOpen(true);
  };

  // POS-I170: the fee the chosen zone quotes for the picked sale; a different fee, or no zone, needs a reason.
  const pickedSaleTotal = (salesData?.data ?? []).find((s: SaleForDelivery) => s.id === form.saleId)?.totalAmount;
  const zoneFeeFor = (zoneId: string): number | null => {
    const z = (zonesData?.data ?? []).find((x: DeliveryZone) => x.id === zoneId);
    if (!z) return null;
    const free = z.freeAboveAmount != null && Number(z.freeAboveAmount) > 0 && pickedSaleTotal != null && Number(pickedSaleTotal) >= Number(z.freeAboveAmount);
    return free ? 0 : Number(z.deliveryFee);
  };
  // POS-I177: the chosen zone's minimum basket (home delivery only; the API checks it too)
  const pickedZone = (zonesData?.data ?? []).find((x: DeliveryZone) => x.id === form.zoneId);
  const zoneMinShortfall = form.orderType === 'DELIVERY' && pickedZone && pickedZone.minOrderAmount != null && pickedSaleTotal != null && Number(pickedSaleTotal) < Number(pickedZone.minOrderAmount)
    ? `Zone ${pickedZone.name} has a minimum order of ${formatCurrency(Number(pickedZone.minOrderAmount))}. This order is ${formatCurrency(Number(pickedSaleTotal))}.`
    : null;
  const feeNeedsReason = form.orderType === 'DELIVERY' && zoneOptions.length > 0
    && (!form.zoneId || (zoneFeeFor(form.zoneId) !== null && Number(form.deliveryFee || 0) !== zoneFeeFor(form.zoneId)));

  const orderTypeFilterChips = [
    { key: 'ALL', label: t('deliveryFilterAllOrders') },
    { key: 'DELIVERY', label: t('deliveryFilterDelivery') },
    { key: 'CLICK_AND_COLLECT', label: t('deliveryFilterClickCollect') },
  ];

  // POS-I159: a Click and Collect order marked Ready is stored as ASSIGNED; show it as Ready for pickup.
  const statusFilterChips = [
    { key: '', label: t('deliveryFilterAllStatuses') },
    ...DELIVERY_STATUSES.map((s) => ({
      key: s,
      label: s === 'ASSIGNED' && orderTypeTab === 'CLICK_AND_COLLECT' ? t('deliveryReadyForPickup') : STATUS_LABELS[s],
    })),
  ];

  const isClickCollect = (order: DeliveryOrder) => order.orderType === 'CLICK_AND_COLLECT';
  const statusLabel = (order: DeliveryOrder) =>
    isClickCollect(order) && order.status === 'ASSIGNED' ? t('deliveryReadyForPickup') : (STATUS_LABELS[order.status] ?? order.status);
  const invoiceLabel = (order: DeliveryOrder) =>
    (order.source === 'DINING' ? (order.orderNo ?? order.sale?.invoiceNumber) : order.sale?.invoiceNumber) ?? '';

  const mainTabs = [
    { key: 'orders', label: t('deliveryNavOrders'), icon: Package },
    { key: 'drivers', label: t('deliveryNavDrivers'), icon: Car },
    { key: 'zones', label: t('deliveryNavZones'), icon: Map },
  ];

  if (!canRead) {
    return <div className="p-8 text-center text-muted-foreground">{t('noPermissionDelivery')}</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('deliveryTitle')}
        subtitle={t('deliverySubtitle')}
        actions={
          mainTab === 'orders' && canCreate ? (
            <Button onClick={() => setDialogOpen(true)} className="gap-2" variant="gradient">
              <Plus className="h-4 w-4" /> {t('newDeliveryOrder')}
            </Button>
          ) : mainTab === 'drivers' && canManage ? (
            <Button onClick={() => { setEditingDriver(null); setDriverForm(emptyDriverForm()); setDriverDialogOpen(true); }} className="gap-2" variant="gradient">
              <Plus className="h-4 w-4" /> Add Driver
            </Button>
          ) : mainTab === 'zones' && canManage ? (
            <Button onClick={() => { setEditingZone(null); setZoneForm(emptyZoneForm()); setZoneDialogOpen(true); }} className="gap-2" variant="gradient">
              <Plus className="h-4 w-4" /> Add Zone
            </Button>
          ) : undefined
        }
      />

      <Card className="animate-fade-in-up">
        <div className="border-b border-border">
          <TabBar tabs={mainTabs} activeTab={mainTab} onTabChange={(k) => setMainTab(k as MainTab)} />
        </div>

        {mainTab === 'orders' ? (
          <>
            <div className="border-b border-border px-4 py-3 space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Switch id="delivery-notices" aria-label="Customer notices by SMS" checked={deliverySettings?.noticesEnabled ?? true}
                  disabled={!canManage || noticesMutation.isPending || !deliverySettings}
                  onCheckedChange={(v) => noticesMutation.mutate(v)} />
                <Label htmlFor="delivery-notices" className="mb-0">Tell customers by SMS when their order is on its way, delivered, failed or ready for pickup</Label>
                <span className="text-xs text-muted-foreground">Edit the wording in Communications. Nothing is sent until an SMS provider is enabled.</span>
              </div>
              <FilterChips
                chips={orderTypeFilterChips}
                value={orderTypeTab}
                onChange={(k) => { setOrderTypeTab(k as OrderTypeTab); setPage(1); }}
                variant="outline"
                size="sm"
              />
              <FilterChips
                chips={statusFilterChips}
                value={statusFilter}
                onChange={(k) => { setStatusFilter(k); setPage(1); }}
                variant="outline"
                size="sm"
              />
            </div>
            <CardContent className="pt-4">
              {isLoading ? (
                <SkeletonTable rows={8} cols={8} />
              ) : !data?.data?.length ? (
                <EmptyState
                  icon={orderTypeTab === 'CLICK_AND_COLLECT' ? ShoppingBag : Truck}
                  title={orderTypeTab === 'CLICK_AND_COLLECT' ? 'No Click & Collect orders' : t('noDeliveryOrdersFound')}
                  description={statusFilter ? t('noOrdersWithStatus', { status: STATUS_LABELS[statusFilter] }) : t('createFirstDelivery')}
                  action={canCreate ? { label: t('newDeliveryOrder'), onClick: () => setDialogOpen(true) } : undefined}
                />
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border">
                    <Table mobileCards className="[&_td]:px-2 [&_th]:px-2">
                      <THead>
                        <Tr hover={false}>
                          <Th>{t('tblType')}</Th>
                          <Th>{t('colInvoice')}</Th>
                          <Th>{t('colCustomer')}</Th>
                          <Th className="text-end">Total</Th>
                          <Th>{t('colAddress')}</Th>
                          <Th>{t('tblDriver')}</Th>
                          <Th className="text-center">{t('colStatus')}</Th>
                          {canManage && <Th className="sticky right-0 z-10 bg-card shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">{t('tblActions')}</Th>}
                        </Tr>
                      </THead>
                      <TBody>
                        {data.data.map((order: DeliveryOrder) => (
                          <Tr key={order.id}>
                            <Td label={t('tblType')}>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${isClickCollect(order) ? 'bg-primary/10 text-primary-text' : 'bg-info/10 text-info-text'}`}>
                                {isClickCollect(order) ? <><ShoppingBag className="h-3 w-3" /> C&C</> : <><Truck className="h-3 w-3" /> Delivery</>}
                              </span>
                            </Td>
                            <Td className="font-mono text-xs font-medium" label={t('colInvoice')}>
                              {order.source === 'DINING'
                                ? (order.orderNo ?? order.sale?.invoiceNumber ?? '—')
                                : (order.sale?.invoiceNumber ?? '—')}
                              <div className="font-sans text-[11px] font-normal text-muted-foreground" title={t('colCreated')}>{formatDate(order.createdAt)}</div>
                            </Td>
                            <Td className="text-sm" label={t('colCustomer')}>
                              {order.sale?.customer?.name ?? 'Walk-in'}
                              {(order.customerPhone || order.sale?.customer?.phone) && (
                                <div className="text-xs text-muted-foreground" dir="ltr">{order.customerPhone || order.sale?.customer?.phone}</div>
                              )}
                              {order.lastNotice && (
                                <div className={`text-xs ${order.lastNotice.text === 'Sent' ? 'text-success-text' : 'text-warning-text'}`}>SMS: {order.lastNotice.text}</div>
                              )}
                            </Td>
                            <Td className="text-sm text-end font-mono" label="Total">
                              {order.sale?.totalAmount != null ? formatCurrency(Number(order.sale.totalAmount)) : '—'}
                            </Td>
                            <Td className="text-sm text-muted-foreground max-w-[220px] break-words" label={t('colAddress')}>
                              {isClickCollect(order)
                                ? <span className="italic text-primary-text">Pickup @ {locationRows.find((l) => l.id === order.pickupLocationId)?.name ?? 'Store'}{order.pickupReadyAt ? ` · Ready ${formatDate(order.pickupReadyAt)}` : ''}</span>
                                : [order.address, order.city].filter(Boolean).join(', ') || '—'}
                              {!isClickCollect(order) && Number(order.deliveryFee ?? 0) > 0 && (
                                <div className="text-xs">
                                  Delivery fee {formatCurrency(Number(order.deliveryFee))} ·{' '}
                                  {order.paymentMode === 'COD'
                                    ? (order.feeBilled === false ? 'not billed to the customer' : 'billed to the customer, collected in cash')
                                    : 'not billed on the sale'}
                                  {order.feeReason ? ` · entered by hand: ${order.feeReason}` : ''}
                                </div>
                              )}
                              {order.paymentMode === 'COD' && order.cod && (
                                <div className="text-xs font-medium text-primary-text">
                                  Cash on delivery{order.cod.amountToCollect > 0 ? ` · Collect ${formatCurrency(order.cod.amountToCollect)}` : ' · fully collected'}
                                  {order.cod.cashCollected > 0 && order.cod.amountToCollect > 0 ? ` (collected ${formatCurrency(order.cod.cashCollected)})` : ''}
                                  {order.cod.deferredReason ? ` · Collected later: ${order.cod.deferredReason}` : ''}
                                </div>
                              )}
                            </Td>
                            <Td className="text-sm" label={t('tblDriver')}>
                              {order.driver ? (
                                <span className="flex flex-wrap items-center gap-1 text-xs">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                  {order.driver.name}
                                  {canManage && !isClickCollect(order) && !['DELIVERED', 'CANCELLED'].includes(order.status) && (
                                    <Button variant="ghost" size="sm" className="text-xs h-6 text-muted-foreground"
                                      onClick={() => { setAssignDriverDialog({ orderId: order.id, invoice: invoiceLabel(order), currentDriverId: order.driver?.id ?? null }); setSelectedDriver(order.driver?.id ?? ''); }}>
                                      Change
                                    </Button>
                                  )}
                                </span>
                              ) : (
                                canManage && !isClickCollect(order) && !['DELIVERED', 'CANCELLED'].includes(order.status) ? (
                                  <Button variant="ghost" size="sm" className="text-xs h-6 text-muted-foreground" onClick={() => { setAssignDriverDialog({ orderId: order.id, invoice: invoiceLabel(order), currentDriverId: null }); setSelectedDriver(''); }}>
                                    + Assign
                                  </Button>
                                ) : <span className="text-muted-foreground">—</span>
                              )}
                            </Td>
                            <Td className="text-center" label={t('colStatus')}>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] ?? ''}`}>
                                {statusLabel(order)}
                              </span>
                              {order.deliveredAt && (
                                <div className="text-xs text-success-text mt-0.5">Delivered {formatDate(order.deliveredAt)}</div>
                              )}
                              {order.proof?.receivedBy && (
                                <div className="text-xs text-muted-foreground mt-0.5">Received by {order.proof.receivedBy}</div>
                              )}
                              {order.proof?.fileKey && (
                                <button type="button" className="block mx-auto text-xs text-muted-foreground underline mt-0.5"
                                  onClick={async () => { try { window.open(URL.createObjectURL(await fetchProofImage(order.proof?.fileKey ?? '')), '_blank'); } catch (err) { notify.error(err, 'Could not open the proof'); } }}>
                                  View proof
                                </button>
                              )}
                              <button type="button" className="block mx-auto text-xs text-muted-foreground underline mt-0.5" onClick={() => setHistoryOrderId(order.id)}>History</button>
                              {!isClickCollect(order) && order.source !== 'DINING' && (
                                <button type="button" className="block mx-auto text-xs text-muted-foreground underline mt-0.5"
                                  onClick={() => printDeliveryNote(order.id).catch((err) => notify.error(err, 'Could not print the delivery note'))}>
                                  Delivery note
                                </button>
                              )}
                              {isClickCollect(order) && order.pickedUpAt && (
                                <div className="text-xs text-success-text mt-0.5">Picked up {formatDate(order.pickedUpAt)}</div>
                              )}
                            </Td>
                                                        {canManage && (
                              <Td label={t('tblActions')} className="sticky right-0 z-10 bg-card shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                                <div className="flex flex-wrap items-center gap-1.5 min-w-[130px]">
                                  {isClickCollect(order) && order.status === 'PENDING' && !order.pickupReadyAt && (
                                    <Button variant="outline" size="sm" className="text-xs h-7"
                                      onClick={() => updateMutation.mutate({ id: order.id, status: 'ASSIGNED', pickupReadyAt: new Date().toISOString() })}>
                                      <CheckCircle className="h-3 w-3 me-1" /> Ready
                                    </Button>
                                  )}
                                  {isClickCollect(order) && order.status !== 'DELIVERED' && !order.pickedUpAt && order.pickupReadyAt && (
                                    <Button variant="outline" size="sm" className="text-xs h-7 border-success text-success-text hover:bg-success/10"
                                      onClick={() => updateMutation.mutate({ id: order.id, status: 'DELIVERED', pickedUp: true })}>
                                      <CheckCircle className="h-3 w-3 me-1" /> Picked Up
                                    </Button>
                                  )}
                                  {order.paymentMode === 'COD' && (order.cod?.amountToCollect ?? 0) > 0 && ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'DELIVERED'].includes(order.status) && (
                                    <Button variant="outline" size="sm" className="text-xs h-7"
                                      onClick={() => openCashDialog(order, false)}>
                                      Collect cash
                                    </Button>
                                  )}
                                  {!isClickCollect(order) && (
                                    <Dropdown value={order.status}
                                      disabled={(NEXT_STATUSES[order.status] ?? []).length === 0}
                                      onChange={(e) => (e.value === 'FAILED' || e.value === 'CANCELLED')
                                        ? setConfirmStatusChange({ id: order.id, status: e.value, reason: '' })
                                        : e.value === 'DELIVERED' && order.source !== 'DINING'
                                          ? openCashDialog(order, true)
                                          : updateMutation.mutate({ id: order.id, status: e.value })}
                                      options={[order.status, ...(NEXT_STATUSES[order.status] ?? [])].map((s) => ({ label: STATUS_LABELS[s] ?? s, value: s }))}
                                      optionLabel="label"
                                      optionValue="value"
                                      appendTo={typeof document !== 'undefined' ? document.body : undefined}
                                      className="text-xs" />
                                  )}
                                </div>
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                  {(data?.total ?? 0) > 20 && (
                    <div className="mt-4 border-t pt-4">
                      <PaginationBar page={page} pageSize={20} total={data.total} onPageChange={setPage} />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </>
        ) : mainTab === 'drivers' ? (
          /* Drivers tab */
          <CardContent className="pt-4">
            {driversLoading ? (
              <SkeletonTable rows={5} cols={6} />
            ) : !(driversData?.data?.length) ? (
              <EmptyState
                icon={User}
                title="No drivers yet"
                description="Add delivery drivers to assign them to orders"
                action={canManage ? { label: 'Add Driver', onClick: () => setDriverDialogOpen(true) } : undefined}
              />
            ) : (
              <div>
              <FilterChips chips={ACTIVE_FILTER_CHIPS} value={driverFilter} onChange={(k) => setDriverFilter(k as ActiveFilter)} className="mb-3" />
              <div className="overflow-x-auto rounded-lg border">
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('tblName')}</Th>
                      <Th>{t('tblPhone')}</Th>
                      <Th>{t('tblVehicle')}</Th>
                      <Th>{t('tblLicense')}</Th>
                      <Th className="text-center">{t('tblOrders')}</Th>
                      <Th className="text-center">{t('tblStatus')}</Th>
                      {canManage && <Th>{t('tblActions')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {driversData.data.filter((d: DeliveryDriver) => passesActiveFilter(d.isActive, driverFilter)).map((driver: DeliveryDriver) => (
                      <Tr key={driver.id} className={driver.isActive ? undefined : 'opacity-60'}>
                        <Td className="font-medium" label={t('tblName')}>{driver.name}</Td>
                        <Td className="text-sm" label={t('tblPhone')}>{driver.phone}</Td>
                        <Td className="text-sm text-muted-foreground" label={t('tblVehicle')}>
                          {[driver.vehicleType, driver.vehicleNo].filter(Boolean).join(' · ') || '—'}
                        </Td>
                        <Td className="text-sm" label={t('tblLicense')}>{driver.licenseNo ?? '—'}</Td>
                        <Td className="text-center text-sm" label={t('tblOrders')}>{driver._count?.deliveryOrders ?? 0}</Td>
                        <Td className="text-center" label={t('tblStatus')}>
                          <StatusBadge status={driver.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        </Td>
                        {canManage && (
                          <Td className="text-end td-actions">
                            <TableRowActions
                              actions={[
                                {
                                  key: 'edit',
                                  label: 'Edit driver',
                                  icon: Pencil,
                                  onSelect: () => handleEditDriver(driver),
                                  primary: true,
                                },
                                {
                                  key: 'toggle',
                                  label: driver.isActive ? 'Deactivate driver' : 'Activate driver',
                                  icon: driver.isActive ? PowerOff : Power,
                                  onSelect: () => requestDriverActive(driver, !driver.isActive),
                                },
                                {
                                  key: 'delete',
                                  label: 'Delete driver',
                                  icon: Trash2,
                                  onSelect: () => setConfirmDeleteDriver(driver.id),
                                  variant: 'destructive',
                                },
                              ]}
                            />
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
              </div>
            )}
          </CardContent>
        ) : (
          /* Zones tab */
          <CardContent className="pt-4">
            {zonesLoading ? (
              <SkeletonTable rows={4} cols={5} />
            ) : !(zonesData?.data?.length) ? (
              <EmptyState
                icon={Truck}
                title="No delivery zones yet"
                description="Define zones to auto-calculate delivery fees"
                action={canManage ? { label: 'Add Zone', onClick: () => setZoneDialogOpen(true) } : undefined}
              />
            ) : (
              <div>
              <FilterChips chips={ACTIVE_FILTER_CHIPS} value={zoneFilter} onChange={(k) => setZoneFilter(k as ActiveFilter)} className="mb-3" />
              <div className="overflow-x-auto rounded-lg border">
                <Table mobileCards className="[&_td]:px-2 [&_th]:px-2">
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('tblZoneName')}</Th>
                      <Th>{t('tblCitiesAreas')}</Th>
                      <Th className="text-end">{t('tblFee')}</Th>
                      <Th className="text-end">{t('tblFreeAbove')}</Th>
                      <Th className="text-end">Min order</Th>
                      <Th>Delivery hours</Th>
                      <Th className="text-center">{t('tblEtaMin')}</Th>
                      <Th className="text-center">{t('tblStatus')}</Th>
                      {canManage && <Th>{t('tblActions')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {(zonesData?.data ?? []).filter((z: DeliveryZone) => passesActiveFilter(z.isActive, zoneFilter)).map((zone: DeliveryZone) => (
                      <Tr key={zone.id} className={zone.isActive ? undefined : 'opacity-60'}>
                        <Td className="font-medium" label={t('tblZoneName')}>{zone.name}</Td>
                        <Td className="text-sm text-muted-foreground whitespace-normal max-w-[14rem]" label={t('tblCitiesAreas')}>{(zone.cities ?? []).join(', ') || '—'}</Td>
                        <Td className="text-end font-mono text-sm" label={t('tblFee')}>{formatCurrency(zone.deliveryFee)}</Td>
                        <Td className="text-end font-mono text-sm" label={t('tblFreeAbove')}>{zone.freeAboveAmount ? formatCurrency(zone.freeAboveAmount) : '—'}</Td>
                        <Td className="text-end font-mono text-sm" label="Min order">{zone.minOrderAmount && Number(zone.minOrderAmount) > 0 ? formatCurrency(Number(zone.minOrderAmount)) : '—'}</Td>
                        <Td className="text-sm whitespace-normal" label="Delivery hours">{zoneHoursText(zone) || 'Always'}</Td>
                        <Td className="text-center text-sm" label={t('tblEtaMin')}>{zone.estimatedMinutes ?? '—'}</Td>
                        <Td className="text-center" label={t('tblStatus')}>
                          <StatusBadge status={zone.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        </Td>
                        {canManage && (
                          <Td className="text-end td-actions !px-1 [&_button]:!h-7 [&_button]:!w-7">
                            <TableRowActions
                              actions={[
                                {
                                  key: 'edit',
                                  label: 'Edit zone',
                                  icon: Pencil,
                                  onSelect: () => handleEditZone(zone),
                                  primary: true,
                                },
                                {
                                  key: 'toggle',
                                  label: zone.isActive ? 'Deactivate zone' : 'Activate zone',
                                  icon: zone.isActive ? PowerOff : Power,
                                  onSelect: () => setZoneActiveMutation.mutate({ id: zone.id, isActive: !zone.isActive }),
                                },
                                {
                                  key: 'delete',
                                  label: 'Delete zone',
                                  icon: Trash2,
                                  onSelect: () => setConfirmDeleteZone(zone.id),
                                  variant: 'destructive',
                                },
                              ]}
                            />
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Create Delivery Order Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => { setDialogOpen(false); setForm(emptyDeliveryForm()); setZoneHint(null); setZoneWarn(null); }}
        header={t('newDeliveryOrder')}
        style={{ width: '540px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setDialogOpen(false); setForm(emptyDeliveryForm()); setZoneHint(null); setZoneWarn(null); }}>{t('cancelBtn')}</Button>
            <Button disabled={createMutation.isPending || !form.saleId || !isValidPkMobile(form.phone) || (form.orderType === 'DELIVERY' && !form.address.trim()) || (feeNeedsReason && !form.feeReason.trim()) || !!zoneMinShortfall} onClick={() => createMutation.mutate({ d: form })}>
              {createMutation.isPending ? t('creating') : t('create')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 pt-2">
          <div>
            <Label className="block mb-2">Order Type</Label>
            <div className="flex rounded-lg border overflow-hidden">
              <button type="button" onClick={() => setForm({ ...form, orderType: 'DELIVERY' })}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${form.orderType === 'DELIVERY' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                <Truck className="h-4 w-4" /> Delivery
              </button>
              <button type="button" onClick={() => setForm({ ...form, orderType: 'CLICK_AND_COLLECT' })}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${form.orderType === 'CLICK_AND_COLLECT' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                <ShoppingBag className="h-4 w-4" /> Click & Collect
              </button>
            </div>
          </div>
          <div>
            <Label className="block mb-1">{t('saleInvoiceLabel')}</Label>
            <Dropdown value={form.saleId} options={saleOptions} optionLabel="label" optionValue="value" optionDisabled="disabled" onChange={(e) => {
                const sale = (salesData?.data ?? []).find((s: SaleForDelivery) => s.id === e.value);
                // POS-I179: switching the sale replaces the prefilled mobile (a typed one is kept) and re-quotes the zone fee
                const z = form.zoneId ? (zonesData?.data ?? []).find((x: DeliveryZone) => x.id === form.zoneId) : undefined;
                const quoted = z ? (z.freeAboveAmount != null && Number(z.freeAboveAmount) > 0 && sale?.totalAmount != null && Number(sale.totalAmount) >= Number(z.freeAboveAmount) ? 0 : Number(z.deliveryFee)) : null;
                setForm({ ...form, saleId: e.value, ...phoneForSaleChange(form, sale?.customer?.phone), deliveryFee: feeForSaleChange(form, quoted) });
              }}
              placeholder={t('selectSalePlaceholder')} filter filterPlaceholder={t('searchSalePlaceholder')} className="w-full" style={{ width: '100%' }} />
          </div>
          <div>
            <Label className="block mb-1">Customer mobile <span className="text-destructive-text">*</span></Label>
            <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value, phoneEdited: true })} placeholder="03XXXXXXXXX" inputMode="tel" />
            {form.phone.trim() && !isValidPkMobile(form.phone) && (
              <p className="mt-1 text-xs text-destructive-text">Enter a Pakistani mobile number such as 03XXXXXXXXX. The customer is told by SMS when the order moves.</p>
            )}
          </div>
          {form.orderType === 'DELIVERY' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="block mb-1">{t('addressLabel')} <span className="text-destructive-text">*</span></Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t('streetAddressPlaceholder')} />
                </div>
                <div>
                  <Label className="block mb-1">{t('cityLabel')}</Label>
                  <div className="flex gap-2">
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t('cityPlaceholder')} />
                    {zoneOptions.length > 0 && (
                      <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5 whitespace-nowrap"
                        disabled={detectingZone || !form.city.trim()} onClick={autoDetectZone}>
                        {detectingZone ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
                        Auto-detect
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="block mb-1">Delivery fee (Rs)</Label>
                  <Input type="number" min="0" step="1" value={form.deliveryFee || ''} placeholder="0"
                    onChange={(e) => setForm({ ...form, deliveryFee: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="block mb-1">Payment</Label>
                  <div className="flex rounded-lg border overflow-hidden">
                    <button type="button" onClick={() => setForm({ ...form, paymentMode: 'PREPAID' })}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${form.paymentMode === 'PREPAID' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>Prepaid</button>
                    <button type="button" onClick={() => setForm({ ...form, paymentMode: 'COD' })}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${form.paymentMode === 'COD' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>Cash on delivery</button>
                  </div>
                </div>
              </div>
              {form.paymentMode === 'COD' && (
                <div className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <Switch id="fee-billed" aria-label="Delivery fee billed to customer" checked={form.feeBilled} onCheckedChange={(v) => setForm({ ...form, feeBilled: v })} />
                  <div>
                    <Label htmlFor="fee-billed" className="mb-0">Delivery fee billed to customer</Label>
                    <p className="text-xs text-muted-foreground">
                      {form.feeBilled
                        ? 'The rider collects the sale balance plus the delivery fee. The fee is booked as Delivery Income only when the cash is recorded.'
                        : 'The rider collects the sale balance only. No delivery income is booked.'}
                    </p>
                  </div>
                </div>
              )}
              {zoneOptions.length > 0 && (
                <div>
                  <Label className="block mb-1">Delivery Zone (auto-calculates fee)</Label>
                  <Dropdown value={form.zoneId} options={[{ value: '', label: '— No zone —' }, ...zoneOptions]} optionLabel="label" optionValue="value"
                    onChange={(e) => {
                      const fee = e.value ? zoneFeeFor(e.value) : null;
                      setForm({ ...form, zoneId: e.value, ...(fee !== null && { deliveryFee: fee }), feeReason: '' });
                      setZoneHint(null);
                      if (e.value) setZoneWarn(null);
                    }}
                    placeholder="Select zone" className="w-full" style={{ width: '100%' }} />
                  {zoneHint && <p className="mt-1 text-xs text-success-text">{zoneHint}</p>}
                  {zoneWarn && <p className="mt-1 text-xs text-warning-text" role="alert">{zoneWarn}</p>}
                  {zoneMinShortfall && <p className="mt-1 text-xs text-destructive-text" role="alert">{zoneMinShortfall} Choose another zone, add items to the sale, or use Click and Collect.</p>}
                  {pickedZone && zoneHoursText(pickedZone) && !zoneMinShortfall && <p className="mt-1 text-xs text-muted-foreground">Delivery hours: {zoneHoursText(pickedZone)}</p>}
                </div>
              )}
              {feeNeedsReason && (
                <div>
                  <Label className="block mb-1">
                    {form.zoneId ? 'Why is the fee different from the zone fee?' : 'Why is the fee entered by hand (no zone)?'} <span className="text-destructive-text">*</span>
                  </Label>
                  <Input value={form.feeReason} maxLength={200} onChange={(e) => setForm({ ...form, feeReason: e.target.value })}
                    placeholder="e.g. Courier to another city, agreed with the customer" />
                  <p className="mt-1 text-xs text-muted-foreground">Saved with the order and shown in its History.</p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="block mb-1">Pickup Location</Label>
                <Dropdown value={form.pickupLocationId} options={locationOptions} optionLabel="label" optionValue="value" onChange={(e) => setForm({ ...form, pickupLocationId: e.value })}
                  placeholder="Select store/warehouse for pickup" className="w-full" style={{ width: '100%' }} />
              </div>
              <div>
                <Label className="block mb-1">Ready for Pickup By (optional)</Label>
                <Input type="datetime-local" value={form.pickupReadyAt} onChange={(e) => setForm({ ...form, pickupReadyAt: e.target.value })} />
              </div>
            </div>
          )}
          <div>
            <Label className="block mb-1">{t('notesLabel')}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} className="resize-none"
              placeholder={t('deliveryInstructionsPlaceholder')} />
          </div>
        </div>
      </Dialog>

      {/* Driver Create/Edit Dialog */}
      <Dialog
        visible={driverDialogOpen}
        onHide={() => { setDriverDialogOpen(false); setEditingDriver(null); setDriverForm(emptyDriverForm()); }}
        header={editingDriver ? 'Edit Driver' : 'Add Driver'}
        style={{ width: '480px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setDriverDialogOpen(false); setEditingDriver(null); setDriverForm(emptyDriverForm()); }}>Cancel</Button>
            <Button
              disabled={driverSaveDisabled}
              onClick={saveDriver}
            >
              {(createDriverMutation.isPending || updateDriverMutation.isPending) ? 'Saving…' : (editingDriver ? 'Save Changes' : 'Add Driver')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pt-2" onKeyDown={submitOnEnter(saveDriver)}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1">Name *</Label>
              <Input value={driverForm.name} onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })} placeholder="Driver name" />
            </div>
            <div>
              <Label className="block mb-1">Phone *</Label>
              <Input dir="ltr" value={driverForm.phone} onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })} placeholder="03xx-xxxxxxx" />
              {driverForm.phone.trim() && !driverPhoneOk && (
                <p className="mt-1 text-xs text-destructive-text">Enter a valid Pakistani mobile number, for example 03XXXXXXXXX.</p>
              )}
            </div>
            <div>
              <Label className="block mb-1">Email</Label>
              <Input value={driverForm.email} onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })} placeholder="driver@example.com" />
              {!driverEmailOk && (
                <p className="mt-1 text-xs text-destructive-text">Enter a valid email, for example name@example.com.</p>
              )}
            </div>
            <div>
              <Label className="block mb-1">Vehicle Type</Label>
              <Input value={driverForm.vehicleType} onChange={(e) => setDriverForm({ ...driverForm, vehicleType: e.target.value })} placeholder="Motorcycle / Car / Van" />
            </div>
            <div>
              <Label className="block mb-1">Vehicle Number</Label>
              <Input value={driverForm.vehicleNo} onChange={(e) => setDriverForm({ ...driverForm, vehicleNo: e.target.value })} placeholder="ABC-123" />
            </div>
            <div>
              <Label className="block mb-1">License No.</Label>
              <Input value={driverForm.licenseNo} onChange={(e) => setDriverForm({ ...driverForm, licenseNo: e.target.value })} placeholder="License number" />
            </div>
          </div>
          <div>
            <Label className="block mb-1">Notes</Label>
            <Textarea value={driverForm.notes} onChange={(e) => setDriverForm({ ...driverForm, notes: e.target.value })}
              rows={2} className="resize-none" placeholder="Optional notes" />
          </div>
          {editingDriver && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="block">Active</Label>
                <p className="text-xs text-muted-foreground">Inactive drivers are not offered when assigning an order.</p>
              </div>
              <Switch checked={driverForm.isActive} onCheckedChange={(v) => setDriverForm({ ...driverForm, isActive: v })} aria-label="Driver active" />
            </div>
          )}
        </div>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog
        visible={!!assignDriverDialog}
        onHide={() => { setAssignDriverDialog(null); setSelectedDriver(''); }}
        header={assignDriverDialog?.currentDriverId ? `Change Driver${assignDriverDialog.invoice ? ` · ${assignDriverDialog.invoice}` : ''}` : `Assign Driver${assignDriverDialog?.invoice ? ` · ${assignDriverDialog.invoice}` : ''}`}
        style={{ width: '380px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setAssignDriverDialog(null); setSelectedDriver(''); }}>Cancel</Button>
            <Button
              disabled={assignDriverMutation.isPending || (!selectedDriver && !assignDriverDialog?.currentDriverId)}
              variant={!selectedDriver && assignDriverDialog?.currentDriverId ? 'destructive' : undefined}
              onClick={() => assignDriverDialog && assignDriverMutation.mutate({ orderId: assignDriverDialog.orderId, driverId: selectedDriver || null })}
            >
              {assignDriverMutation.isPending ? 'Saving…' : !selectedDriver && assignDriverDialog?.currentDriverId ? 'Unassign' : 'Assign'}
            </Button>
          </div>
        }
      >
        <div className="pt-2 space-y-3">
          <p className="text-sm text-muted-foreground">
            {assignDriverDialog?.currentDriverId
              ? `Pick another driver for ${assignDriverDialog.invoice || 'this order'}, or choose Unassign to remove the driver.`
              : `Select a driver for ${assignDriverDialog?.invoice || 'this delivery order'}.`}
          </p>
          <Dropdown
            value={selectedDriver}
            options={[...(assignDriverDialog?.currentDriverId ? [{ value: '', label: '— Unassign —' }] : []), ...driverOptions]} optionLabel="label" optionValue="value"
            onChange={(e) => setSelectedDriver(e.value)}
            placeholder="Select driver"
            className="w-full"
            style={{ width: '100%' }}
          />
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteDriver}
        title={`Remove ${(driversData?.data ?? []).find((d: DeliveryDriver) => d.id === confirmDeleteDriver)?.name ?? 'Driver'}`}
        message={`Are you sure you want to remove ${(driversData?.data ?? []).find((d: DeliveryDriver) => d.id === confirmDeleteDriver)?.name ?? 'this driver'}? This permanently deletes the driver profile.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => { if (confirmDeleteDriver) deleteDriverMutation.mutate(confirmDeleteDriver); setConfirmDeleteDriver(null); }}
        onCancel={() => setConfirmDeleteDriver(null)}
      />

      <ConfirmDialog
        open={!!confirmDeactivateDriver}
        title={`Deactivate ${confirmDeactivateDriver?.driver.name ?? 'driver'}`}
        message={`${confirmDeactivateDriver?.driver.name ?? 'This driver'} still has ${confirmDeactivateDriver?.driver.openOrders ?? 0} open delivery order${(confirmDeactivateDriver?.driver.openOrders ?? 0) === 1 ? '' : 's'} (not Delivered or Cancelled). They stay assigned to this driver, but the driver will no longer be offered for new orders. Deactivate anyway?`}
        confirmLabel="Deactivate"
        variant="danger"
        onConfirm={() => {
          const c = confirmDeactivateDriver;
          setConfirmDeactivateDriver(null);
          if (!c) return;
          if (c.form) updateDriverMutation.mutate({ id: c.driver.id, form: c.form });
          else setDriverActiveMutation.mutate({ id: c.driver.id, isActive: false });
        }}
        onCancel={() => setConfirmDeactivateDriver(null)}
      />

      <ConfirmDialog
        open={!!outsideHoursAsk}
        title="Outside delivery hours"
        message={outsideHoursAsk ?? ''}
        confirmLabel="Create anyway"
        variant="warning"
        onConfirm={() => { setOutsideHoursAsk(null); createMutation.mutate({ d: form, confirmOutsideHours: true }); }}
        onCancel={() => setOutsideHoursAsk(null)}
      />

      {/* Zone Create/Edit Dialog */}
      <Dialog
        visible={zoneDialogOpen}
        onHide={() => { setZoneDialogOpen(false); setEditingZone(null); setZoneForm(emptyZoneForm()); }}
        header={editingZone ? 'Edit Delivery Zone' : 'Add Delivery Zone'}
        style={{ width: '480px' }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setZoneDialogOpen(false); setEditingZone(null); setZoneForm(emptyZoneForm()); }}>Cancel</Button>
            <Button
              disabled={zoneSaveDisabled}
              onClick={saveZone}
            >
              {(createZoneMutation.isPending || updateZoneMutation.isPending) ? 'Saving…' : (editingZone ? 'Save Changes' : 'Add Zone')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pt-2" onKeyDown={submitOnEnter(saveZone)}>
          <div>
            <Label className="block mb-1">Zone Name *</Label>
            <Input value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} placeholder="e.g. Karachi Central" />
          </div>
          <div>
            <Label className="block mb-1">Cities / Areas (comma-separated)</Label>
            <Input value={zoneForm.cities} onChange={(e) => setZoneForm({ ...zoneForm, cities: e.target.value })} placeholder="Karachi, Gulshan, DHA" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="block mb-1">Fee (PKR) *</Label>
              <Input type="number" min="0" step="1" value={zoneForm.deliveryFee}
                onChange={(e) => setZoneForm({ ...zoneForm, deliveryFee: Number(e.target.value) })} />
              {zoneFeeNegative && <p className="mt-1 text-xs text-destructive-text">The fee cannot be negative. Enter 0 for free delivery.</p>}
            </div>
            <div>
              <Label className="block mb-1">Free Above (PKR)</Label>
              <Input type="number" min="0" step="1" value={zoneForm.freeAboveAmount}
                onChange={(e) => setZoneForm({ ...zoneForm, freeAboveAmount: e.target.value })} placeholder="0 = never" />
              {zoneFreeNegative && <p className="mt-1 text-xs text-destructive-text">Cannot be negative.</p>}
            </div>
            <div>
              <Label className="block mb-1">ETA (min)</Label>
              <Input type="number" min="1" step="5" value={zoneForm.estimatedMinutes}
                onChange={(e) => setZoneForm({ ...zoneForm, estimatedMinutes: e.target.value })} placeholder="30" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="block mb-1">Minimum order (PKR)</Label>
              <Input type="number" min="0" step="1" value={zoneForm.minOrderAmount}
                onChange={(e) => setZoneForm({ ...zoneForm, minOrderAmount: e.target.value })} placeholder="0 = none" />
              {zoneMinNegative && <p className="mt-1 text-xs text-destructive-text">Cannot be negative.</p>}
            </div>
            <div>
              <Label className="block mb-1">Delivers from</Label>
              <Input type="time" dir="ltr" value={zoneForm.hoursFrom} onChange={(e) => setZoneForm({ ...zoneForm, hoursFrom: e.target.value })} />
            </div>
            <div>
              <Label className="block mb-1">Delivers until</Label>
              <Input type="time" dir="ltr" value={zoneForm.hoursTo} onChange={(e) => setZoneForm({ ...zoneForm, hoursTo: e.target.value })} />
            </div>
          </div>
          {zoneHoursHalf && <p className="text-xs text-destructive-text">Enter both a from and an until time, or leave both empty.</p>}
          <div>
            <Label className="block mb-1">Delivery days (none selected = every day)</Label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((w) => {
                const on = zoneForm.deliveryDays.includes(w.d);
                return (
                  <Button key={w.d} type="button" size="sm" variant={on ? 'default' : 'outline'} aria-pressed={on} className="h-7 px-2 text-xs"
                    onClick={() => setZoneForm({ ...zoneForm, deliveryDays: on ? zoneForm.deliveryDays.filter((x) => x !== w.d) : [...zoneForm.deliveryDays, w.d] })}>
                    {w.label}
                  </Button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Times are Pakistan time. Orders outside them can still be created after the cashier confirms.</p>
          </div>
          {editingZone && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="block">Active</Label>
                <p className="text-xs text-muted-foreground">Inactive zones are not offered on the order form or by auto-detect.</p>
              </div>
              <Switch checked={zoneForm.isActive} onCheckedChange={(v) => setZoneForm({ ...zoneForm, isActive: v })} aria-label="Zone active" />
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteZone}
        title={`Delete ${(zonesData?.data ?? []).find((z: DeliveryZone) => z.id === confirmDeleteZone)?.name ?? 'Delivery Zone'}`}
        message={`Are you sure you want to delete the zone ${(zonesData?.data ?? []).find((z: DeliveryZone) => z.id === confirmDeleteZone)?.name ?? ''}? To keep it but stop using it, deactivate it instead.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (confirmDeleteZone) deleteZoneMutation.mutate(confirmDeleteZone); setConfirmDeleteZone(null); }}
        onCancel={() => setConfirmDeleteZone(null)}
      />

      <ConfirmDialog
        open={!!confirmStatusChange}
        title={t('confirmStatusTitle', { status: confirmStatusChange?.status === 'FAILED' ? t('deliveryFailed') : t('deliveryCancelled') })}
        message={t('confirmStatusMsg', { status: confirmStatusChange?.status?.toLowerCase() })}
        confirmLabel={t('confirmBtn')}
        variant="danger"
        extra={
          <Textarea value={confirmStatusChange?.reason ?? ''} rows={2} placeholder="Reason (required)"
            onChange={(e) => setConfirmStatusChange((c) => (c ? { ...c, reason: e.target.value } : c))} />
        }
        onConfirm={() => {
          if (!confirmStatusChange) return;
          if (!confirmStatusChange.reason.trim()) { toast.error('Enter a reason first'); return; }
          updateMutation.mutate({ id: confirmStatusChange.id, status: confirmStatusChange.status, reason: confirmStatusChange.reason.trim() });
          setConfirmStatusChange(null);
        }}
        onCancel={() => setConfirmStatusChange(null)}
      />

      {/* POS-I160 (B): record the cash taken at the door */}
      <Dialog
        visible={!!cashDialog}
        onHide={() => setCashDialog(null)}
        header={cashDialog ? `${cashDialog.deliverAfter ? 'Mark Delivered' : 'Collect cash'} · ${invoiceLabel(cashDialog.order)}` : ''}
        style={{ width: '440px' }}
        footer={
          cashDialog ? (
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCashDialog(null)}>{t('cancelBtn')}</Button>
              <Button
                disabled={collectCashMutation.isPending
                  || (cashDialog.deliverAfter && !cashDialog.receivedBy.trim())
                  || (cashDialogCodDue(cashDialog) && (cashDialog.mode === 'collect' ? !(Number(cashDialog.amount) > 0) || Number(cashDialog.amount) > (cashDialog.order.cod?.amountToCollect ?? 0) : !cashDialog.laterReason.trim()))}
                onClick={() => collectCashMutation.mutate({
                  id: cashDialog.order.id,
                  amount: Number(cashDialog.amount),
                  note: cashDialog.note.trim() || undefined,
                  deliverAfter: cashDialog.deliverAfter,
                  laterReason: cashDialog.laterReason.trim(),
                  mode: cashDialog.mode,
                  codDue: cashDialogCodDue(cashDialog),
                  receivedBy: cashDialog.receivedBy,
                  proofNote: cashDialog.proofNote,
                  proofFile: cashDialog.proofFile,
                })}
              >
                {collectCashMutation.isPending ? 'Saving…' : !cashDialog.deliverAfter ? 'Record cash' : !cashDialogCodDue(cashDialog) ? 'Mark Delivered' : cashDialog.mode === 'collect' ? 'Record cash and mark Delivered' : 'Mark Delivered, collect later'}
              </Button>
            </div>
          ) : undefined
        }
      >
        {cashDialog && (
          <div className="space-y-3 pt-2">
            {cashDialog.deliverAfter && (
              <div className="space-y-3">
                <div>
                  <Label className="block mb-1">Received by <span className="text-destructive-text">*</span></Label>
                  <Input value={cashDialog.receivedBy} onChange={(e) => setCashDialog({ ...cashDialog, receivedBy: e.target.value })} placeholder="Name of the person who took the parcel" />
                </div>
                <div>
                  <Label className="block mb-1">Note (optional)</Label>
                  <Input value={cashDialog.proofNote} onChange={(e) => setCashDialog({ ...cashDialog, proofNote: e.target.value })} placeholder="e.g. Left with the guard" />
                </div>
                <div>
                  <Label className="block mb-1">Photo or signature (optional)</Label>
                  <Input type="file" accept="image/jpeg,image/png" onChange={(e) => setCashDialog({ ...cashDialog, proofFile: e.target.files?.[0] ?? null })} />
                  <p className="mt-1 text-xs text-muted-foreground">JPG or PNG. Saved with the delivery as proof; it cannot be changed afterwards.</p>
                </div>
              </div>
            )}
            {cashDialogCodDue(cashDialog) && (<>
            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="text-lg font-semibold">Collect {formatCurrency(cashDialog.order.cod?.amountToCollect ?? 0)}</div>
              <div className="text-xs text-muted-foreground">
                Sale balance {formatCurrency(cashDialog.order.cod?.saleOutstanding ?? 0)}
                {cashDialog.order.cod?.feeBilled ? ` + delivery fee ${formatCurrency(cashDialog.order.cod?.feeOutstanding ?? 0)}` : ''}
              </div>
            </div>
            {cashDialog.deliverAfter && (
              <div className="flex rounded-lg border overflow-hidden">
                <button type="button" onClick={() => setCashDialog({ ...cashDialog, mode: 'collect' })}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${cashDialog.mode === 'collect' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>Cash collected</button>
                <button type="button" onClick={() => setCashDialog({ ...cashDialog, mode: 'later' })}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${cashDialog.mode === 'later' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>Collected later</button>
              </div>
            )}
            {cashDialog.mode === 'collect' ? (
              <>
                <div>
                  <Label className="block mb-1">Cash collected (Rs)</Label>
                  <Input type="number" min="0" step="0.01" value={cashDialog.amount} onChange={(e) => setCashDialog({ ...cashDialog, amount: e.target.value })} />
                  {Number(cashDialog.amount) > (cashDialog.order.cod?.amountToCollect ?? 0) && (
                    <p className="mt-1 text-xs text-destructive-text">More than is due.</p>
                  )}
                  {Number(cashDialog.amount) > 0 && Number(cashDialog.amount) < (cashDialog.order.cod?.amountToCollect ?? 0) && (
                    <p className="mt-1 text-xs text-warning-text">Partial: {formatCurrency((cashDialog.order.cod?.amountToCollect ?? 0) - Number(cashDialog.amount))} will still be due.</p>
                  )}
                </div>
                <div>
                  <Label className="block mb-1">Note (optional)</Label>
                  <Input value={cashDialog.note} onChange={(e) => setCashDialog({ ...cashDialog, note: e.target.value })} />
                </div>
              </>
            ) : (
              <div>
                <Label className="block mb-1">Why is the cash not collected now? <span className="text-destructive-text">*</span></Label>
                <Textarea rows={2} className="resize-none" value={cashDialog.laterReason} onChange={(e) => setCashDialog({ ...cashDialog, laterReason: e.target.value })} placeholder="e.g. Customer will pay at the shop on Friday" />
              </div>
            )}
            </>)}
          </div>
        )}
      </Dialog>

      <Dialog visible={!!historyOrderId} onHide={() => setHistoryOrderId(null)} header="Status history" style={{ width: '460px' }}>
        {historyOrderId && <DeliveryHistory orderId={historyOrderId} labels={STATUS_LABELS} />}
      </Dialog>
    </div>
  );
}

interface DeliveryHistoryEvent { id: string; kind?: 'status' | 'notice' | 'cash' | 'proof'; fromStatus: string | null; toStatus: string | null; reason: string | null; changedBy: string | null; createdAt: string; eventKey?: string; outcome?: string; to?: string; text?: string; amount?: number; saleAmount?: number; feeAmount?: number; receivedBy?: string; fileName?: string }

const NOTICE_LABELS: Record<string, string> = {
  DELIVERY_ON_THE_WAY: 'On its way',
  DELIVERY_DELIVERED: 'Delivered',
  DELIVERY_FAILED: 'Not delivered',
  PICKUP_READY: 'Ready for pickup',
  PICKUP_DONE: 'Picked up',
};

function DeliveryHistory({ orderId, labels }: { orderId: string; labels: Record<string, string> }) {
  const { data, isLoading } = useQuery<DeliveryHistoryEvent[]>({
    queryKey: ['delivery-history', orderId],
    queryFn: () => api.get<DeliveryHistoryEvent[]>(`/delivery/${orderId}/history`).then((r) => r.data),
    // POS-I169: refetch every time the dialog opens; never show a cached older history.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (!data?.length) return <p className="text-sm text-muted-foreground">No status changes recorded yet.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {data.map((e) => (
        <li key={e.id} className="border-b pb-2">
          {e.kind === 'proof' ? (
            <>
              <div className="font-medium">Proof of delivery: received by {e.receivedBy}</div>
              <div className="text-xs text-muted-foreground">{formatDate(e.createdAt)}{e.changedBy ? ` · ${e.changedBy}` : ''}</div>
              {e.reason && <div className="text-xs">Note: {e.reason}</div>}
              {e.fileName && <div className="text-xs">Photo or signature: {e.fileName}</div>}
            </>
          ) : e.kind === 'cash' ? (
            <>
              <div className="font-medium">Cash collected {formatCurrency(e.amount ?? 0)}</div>
              <div className="text-xs">Sale {formatCurrency(e.saleAmount ?? 0)}{(e.feeAmount ?? 0) > 0 ? ` · delivery fee ${formatCurrency(e.feeAmount ?? 0)}` : ''}</div>
              <div className="text-xs text-muted-foreground">{formatDate(e.createdAt)}{e.changedBy ? ` · ${e.changedBy}` : ''}</div>
              {e.reason && <div className="text-xs">Note: {e.reason}</div>}
            </>
          ) : e.kind === 'notice' ? (
            <>
              <div className="font-medium">SMS to customer: {NOTICE_LABELS[e.eventKey ?? ''] ?? e.eventKey}</div>
              <div className={`text-xs ${e.outcome === 'Sent' ? 'text-success-text' : 'text-warning-text'}`}>{e.outcome}{e.to ? ` · ${e.to}` : ''}</div>
              <div className="text-xs text-muted-foreground">{formatDate(e.createdAt)}{e.changedBy ? ` · ${e.changedBy}` : ''}</div>
              {e.text && <div className="text-xs">{e.text}</div>}
            </>
          ) : (
            <>
              <div className="font-medium">{!e.fromStatus && e.toStatus === 'PENDING' ? 'Order created' : `${e.fromStatus ? `${labels[e.fromStatus] ?? e.fromStatus} → ` : ''}${e.toStatus ? (labels[e.toStatus] ?? e.toStatus) : ''}`}</div>
              <div className="text-xs text-muted-foreground">{formatDate(e.createdAt)}{e.changedBy ? ` · ${e.changedBy}` : ''}</div>
              {e.reason && <div className="text-xs">Reason: {e.reason}</div>}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
